/**
 * Close versus discard (F-JR-25).
 *
 * The X is two different actions wearing one glyph, and which one it is depends
 * entirely on what would be lost. `journalOffersDiscard` in `@ff/domain` owns
 * that decision — this module only names the button the offer carries and the
 * two failure sentences, so the matrix itself is stated once, in the contract
 * layer, and the surface cannot drift from it.
 *
 * The discard order is the part worth reading twice: discard **first closes
 * preserving the draft**, and only then removes it. Reversed, a failure to
 * secure the latest changes would be discovered after the record they belong to
 * had already been deleted, and the two sentences below exist precisely because
 * each step can fail on its own and the user has to be told which.
 */

import type { JournalComposerOrigin } from "@ff/domain";
import { JOURNAL_COMPOSER_COPY, journalOffersDiscard } from "@ff/domain";

export type JournalCloseDecision =
  /** Nothing to lose: close and keep whatever draft exists. */
  | { readonly kind: "closeKeepingDraft" }
  /** Raise "Close this entry?" with the destructive label this origin uses. */
  | { readonly kind: "offerDiscard"; readonly discardLabel: string };

/**
 * A persisted entry's destructive action says "Discard changes"; a new or
 * resumed one says "Discard draft".
 *
 * The words are not interchangeable. On a persisted entry the durable record
 * survives and only the edits go; on a draft the whole thing goes. A user who
 * reads "Discard changes" over a standalone draft has been told they will keep
 * something that is about to be deleted.
 */
export function journalDiscardLabel(origin: JournalComposerOrigin): string {
  return origin === "persisted"
    ? JOURNAL_COMPOSER_COPY.discardChanges
    : JOURNAL_COMPOSER_COPY.discardDraft;
}

export interface CloseDecisionInput {
  readonly origin: JournalComposerOrigin;
  readonly isBlank: boolean;
  readonly hasUnsavedChanges: boolean;
  readonly hasSameIdDraft: boolean;
}

/**
 * The F-JR-25 matrix, delegated to the domain and labelled here.
 *
 * `journalOffersDiscard` is imported rather than re-expressed: draft origin
 * always offers, a new origin offers only when it is not blank, and a persisted
 * origin offers when there are unsaved changes **or** a same-id draft already
 * shadows it — the last clause being the one a local re-implementation always
 * forgets, and the one that decides whether a previously autosaved edit can be
 * thrown away.
 */
export function journalCloseDecision(input: CloseDecisionInput): JournalCloseDecision {
  const offers = journalOffersDiscard({
    origin: input.origin,
    isBlank: input.isBlank,
    hasUnsavedChanges: input.hasUnsavedChanges,
    hasSameIdDraft: input.hasSameIdDraft,
  });
  return offers
    ? { kind: "offerDiscard", discardLabel: journalDiscardLabel(input.origin) }
    : { kind: "closeKeepingDraft" };
}

/**
 * The failure of a discard, in the two shapes it can take.
 *
 * Both surface through the same alert — "The draft is still here" — because
 * both mean the same thing to the user: nothing was removed and the editor is
 * still open. What differs is *why*, and that sentence is the body.
 */
export type JournalDiscardOutcome =
  | { readonly kind: "discarded" }
  | { readonly kind: "failed"; readonly message: string };

export const JOURNAL_DISCARD_FAILURE = Object.freeze({
  /** Step one failed: the latest changes could not be secured. */
  secureFailed: JOURNAL_COMPOSER_COPY.secureBeforeDiscardFailure,
  /** Step two failed: the draft is still on disk, so the editor stays open. */
  discardFailed: JOURNAL_COMPOSER_COPY.discardFailure,
} as const);
